import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame } from "lucide-react";
import { AnimatedNumber } from "@/components/dashboard/AnimatedNumber";
import { Confetti } from "@/components/dashboard/Confetti";
import { cn } from "@/lib/cn";

export type StreakChipProps = {
  days: number;
  /**
   * Optional caption rendered on the chip. The chip defaults to
   * "<n> day streak" pluralisation; the caller can override for
   * milestone moments ("Day 7, Aarav").
   */
  caption?: string;
  /** Compact mode: just the flame + number. Used in tight headers
   *  (e.g. dashboard hero on mobile) where the caption text would
   *  otherwise crowd the title. */
  compact?: boolean;
  className?: string;
};

const MILESTONES = [7, 14, 30, 60, 100] as const;

/**
 * The dashboard's signature flourish (per FRONTEND_PLAN section 2.6):
 *   - Number counts up from 0 -> current on first paint.
 *   - On +1 (day's first heartbeat), the chip pops + soft brand glow.
 *   - On milestone crossings (7, 14, 30...), a confetti-lite burst fires.
 *
 * Built as a self-contained primitive: pass `days` and forget.
 * Persists last seen value in localStorage so a refresh doesn't re-pop.
 */
export function StreakChip({ days, caption, compact, className }: StreakChipProps) {
  const reduce = useReducedMotion();
  const safeDays = Math.max(0, Math.floor(days));

  // Track previous value across sessions to detect "+1 since last visit".
  const STORAGE_KEY = "staxed:streak:last-seen";
  const lastSeenRef = useRef<number | null>(null);
  if (lastSeenRef.current === null && typeof window !== "undefined") {
    const raw = localStorage.getItem(STORAGE_KEY);
    lastSeenRef.current = raw ? parseInt(raw, 10) : 0;
  }

  const [popKey, setPopKey] = useState(0);
  const [confettiTrigger, setConfettiTrigger] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const last = lastSeenRef.current ?? 0;
    if (safeDays > last) {
      setPopKey((k) => k + 1);
      // Milestone crossings -> confetti burst.
      const crossed = MILESTONES.find((m) => last < m && safeDays >= m);
      if (crossed != null) setConfettiTrigger(safeDays);
    }
    localStorage.setItem(STORAGE_KEY, String(safeDays));
    lastSeenRef.current = safeDays;
  }, [safeDays]);

  const label = caption ?? defaultLabel(safeDays);

  return (
    <div
      className={cn(
        "relative inline-flex items-center gap-2 rounded-full",
        "px-3 py-1.5",
        "bg-brand-soft text-brand border border-brand/20",
        "shadow-[0_2px_8px_rgb(249_115_22_/_0.12)]",
        className,
      )}
    >
      <Confetti
        trigger={confettiTrigger ?? 0}
        count={22}
        spread={70}
        origin={{ x: "50%", y: "50%" }}
        label={confettiTrigger ? `Day ${confettiTrigger}` : undefined}
      />

      <motion.span
        key={popKey}
        className="grid place-items-center"
        initial={reduce ? false : { scale: 0.85, rotate: -8 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 360, damping: 16 }}
      >
        <Flame className="h-4 w-4" />
      </motion.span>

      <span className="flex items-baseline gap-1.5 leading-none">
        <AnimatedNumber
          value={safeDays}
          durationMs={680}
          className="font-display text-base sm:text-lg font-bold tabular-nums"
        />
        {compact ? null : (
          <span className="text-xs font-medium opacity-80 whitespace-nowrap">{label}</span>
        )}
      </span>
    </div>
  );
}

function defaultLabel(days: number): string {
  if (days === 0) return "no streak yet";
  return days === 1 ? "day" : "day streak";
}
